"use client";

import { normalizeNumberGrouping } from "@/lib/locale-formatting";

import { artistLinkRel } from "@/lib/seo-enrichment";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import Image from "next/image";
import { List, LayoutGrid, Map as MapIcon, X, Pencil } from "lucide-react";
import { ReportButton } from "../report/report-button";
import { ArtistExternalLinks } from "../artist-external-links";
import { Event, Attraction, MapBounds, FestivalSummary, SimilarArtist } from "@/lib/types";
import { CITIES, getCitySlugForVenueCity } from "@/lib/constants";
import { DateRangeValue } from "@/lib/constants";
import {
  filterByDateRange,
  filterByTimeOfDay,
  groupEventsByVenue,
  isDisplayableGenre,
  TimeOfDay,
  getUniqueGenres,
} from "@/lib/utils";
import { FilterBar } from "./filter-bar";
import { EventsGrid } from "./events-grid";
import { ListSidebar } from "./list-sidebar";
import dynamic from "next/dynamic";
import { MapSidebar } from "../map/map-sidebar";
import { AuthProvider, useAuth } from "../auth/auth-context";
import { SubscribeButton } from "../subscribe-button";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useSetSpotifyCitySlug } from "../spotify/use-set-city-slug";
import { useRelatedJukebox } from "../spotify/use-related-jukebox";
import { UserMenu } from "../auth/user-menu";
import { LanguageSwitcher } from "../language-switcher";
import { SearchButton } from "../search/search-button";
import { User } from "@supabase/supabase-js";
import { ArtistEditModal } from "../admin/artist-edit-modal";
import { PlayIcon } from "@/components/spotify/play-icon";
import { useDiscoveryWebMcp } from "../discovery/use-discovery-webmcp";
import { isDiscoveryArtist, isDiscoveryEvent } from "@/lib/discovery/policy";
import type { PagePublication, PagePublicationBlockedIds } from "@/lib/discovery/page-publication";

const MapView = dynamic(
  () => import("../map/map-view").then((m) => m.MapView),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center bg-surface">
        <p className="font-mono text-xs text-neutral-500">LOADING MAP…</p>
      </div>
    ),
  }
);
const GenreJukeboxModal = dynamic(() => import("./genre-jukebox-modal").then((module) => module.GenreJukeboxModal), { ssr: false });

interface ArtistPageClientProps {
  events: Event[];
  festivals?: FestivalSummary[];
  artist: Attraction;
  biography?: string;
  blockedSpotifyIds: string[];
  discoveryBlockedSpotifyIds?: string[];
  discoveryBlockedSpotifyIdsByCity?: PagePublicationBlockedIds;
  discoveryPublication?: PagePublication;
  initialUser?: User | null;
  similarArtists?: SimilarArtist[];
}

function SpotifyPlayButton({
  spotifyId,
  artistName,
  event,
  attractionId,
}: {
  spotifyId: string;
  artistName: string;
  event?: Event;
  attractionId: number;
}) {
  const { play, artistId } = useSpotifyPlayer();
  const isPlaying = artistId === spotifyId;

  return (
    <button
      id="listen"
      onClick={() => play(spotifyId, artistName, event ? { event, artistId: attractionId } : undefined)}
      aria-label={`Preview ${artistName} on finda.show`}
      className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
        isPlaying
          ? "border-[#1DB954] bg-[#1DB954] text-black"
          : "border-neutral-700 text-[#1DB954] hover:border-[#1DB954] hover:bg-[#1DB954]/10"
      }`}
      title={isPlaying ? "Stop preview" : "Play on Spotify"}
    >
      <PlayIcon className="h-4 w-4" />
    </button>
  );
}

function AdminArtistEditButton({ artist }: { artist: Attraction }) {
  const { isAdmin } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  if (!isAdmin) return null;
  return (
    <>
      <button
        onClick={() => setEditOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-700 text-neutral-500 transition-colors hover:border-acid hover:text-acid"
        title="Edit artist"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {editOpen && (
        <ArtistEditModal
          artist={{
            id: artist.id,
            name: artist.name,
            slug: artist.slug,
            genres: artist.genres,
            spotify_id: artist.spotify_id,
            image_url: artist.image_url,
          }}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

export function ArtistPageClient({
  events,
  festivals,
  artist,
  biography,
  blockedSpotifyIds,
  discoveryBlockedSpotifyIds,
  discoveryBlockedSpotifyIdsByCity,
  discoveryPublication,
  initialUser = null,
  similarArtists = [],
}: ArtistPageClientProps) {
  const t = useTranslations("Events");
  const tJukebox = useTranslations("Jukebox");
  const tCommon = useTranslations("Common");
  const router = useRouter();
  const [genresExpanded, setGenresExpanded] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [view, setView] = useState<"list" | "grid" | "map">("list");
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeOfDay | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const { artistId: spotifyActiveId, startPlayback } = useSpotifyPlayer();
  const spotifyBarVisible = !!spotifyActiveId;

  const filteredEvents = useMemo(() => {
    let result = events;
    result = filterByDateRange(result, dateRange, customStartDate, customEndDate);
    result = filterByTimeOfDay(result, selectedTime);
    return result;
  }, [events, dateRange, customStartDate, customEndDate, selectedTime]);

  const venueGroups = useMemo(
    () => groupEventsByVenue(filteredEvents),
    [filteredEvents]
  );

  // Events visible within current map bounds
  const visibleEvents = useMemo(() => {
    if (!mapBounds) return filteredEvents;
    return filteredEvents.filter((e) => {
      const lat = e.venue.latitude;
      const lng = e.venue.longitude;
      return (
        lat >= mapBounds.south &&
        lat <= mapBounds.north &&
        lng >= mapBounds.west &&
        lng <= mapBounds.east
      );
    });
  }, [filteredEvents, mapBounds]);

  const visibleVenueGroups = useMemo(
    () => groupEventsByVenue(visibleEvents),
    [visibleEvents]
  );

  // Compute map center/zoom from all event venues
  const mapCenter = useMemo(() => {
    if (events.length === 0) return { lat: 0, lng: 0 };
    const lats = events.map((e) => e.venue.latitude);
    const lngs = events.map((e) => e.venue.longitude);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [events]);

  const mapZoom = useMemo(() => {
    if (events.length === 0) return 4;
    const lats = events.map((e) => e.venue.latitude);
    const lngs = events.map((e) => e.venue.longitude);
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const spread = Math.max(latSpread, lngSpread);
    if (spread < 0.05) return 13;
    if (spread < 0.5) return 11;
    if (spread < 2) return 9;
    if (spread < 10) return 6;
    return 4;
  }, [events]);

  // Derive a city slug/name from the first event's venue
  const firstCitySlug = useMemo(() => {
    for (const e of events) {
      const slug = e.venue.city_slug ?? getCitySlugForVenueCity(e.venue.city ?? "");
      if (slug) return slug;
    }
    return "";
  }, [events]);

  const firstCityName = CITIES[firstCitySlug]?.name ?? "";

  useSetSpotifyCitySlug(firstCitySlug);
  const relatedJukebox = useRelatedJukebox(firstCitySlug, blockedSpotifyIds);

  const discoveryBlocked = discoveryBlockedSpotifyIds ?? blockedSpotifyIds;
  const listeningEvent = discoveryPublication
    ? events.find((event) => event.id === discoveryPublication.eventId)
    : discoveryBlockedSpotifyIdsByCity ? undefined : events.find((event) => {
      const citySlug = event.venue.city_slug ?? getCitySlugForVenueCity(event.venue.city ?? "");
      return citySlug && isDiscoveryEvent(event, citySlug, discoveryBlocked)
        && event.attractions.some((billed) => billed.id === artist.id);
    });
  const listeningCity = discoveryPublication?.city ?? (listeningEvent
    ? listeningEvent.venue.city_slug ?? getCitySlugForVenueCity(listeningEvent.venue.city ?? "") : null);
  const listeningBlocked = listeningCity ? discoveryBlockedSpotifyIdsByCity?.[listeningCity] ?? discoveryBlocked : discoveryBlocked;
  const canListen = !!listeningEvent && !!listeningCity && isDiscoveryArtist(artist, listeningBlocked);
  const startArtistPlayer = () => {
    if (!listeningEvent || !artist.spotify_id) return;
    const target = { event: listeningEvent, artistId: artist.id };
    startPlayback(artist.spotify_id, artist.name, target);
    relatedJukebox.openJukebox(target);
  };
  useDiscoveryWebMcp({
    events, artists: [artist], blockedSpotifyIds: discoveryBlocked, blockedSpotifyIdsByCity: discoveryBlockedSpotifyIdsByCity,
    onPreview: (target, event) => {
      startPlayback(target.spotify_id!, target.name, event ? { event, artistId: target.id } : undefined);
    },
    onJukebox: (event, artist) => relatedJukebox.openJukebox({ event, artistId: artist?.id }),
  });

  useEffect(() => {
    if (window.history.length > 1 && document.referrer && document.referrer.startsWith(window.location.origin)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasHistory(true);
    }
  }, []);

  const handleVenueSelect = useCallback((venueId: number | null) => {
    setSelectedVenueId(venueId);
  }, []);

  const handleBoundsChanged = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  return (
    <AuthProvider initialUser={initialUser}>
        <div className="flex min-h-screen flex-col bg-dark font-sans text-white">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-6">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Link
                    href="/"
                    className="flex items-center gap-2 transition-opacity hover:opacity-80"
                  >
                    <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-acid shadow-[0_0_10px_#D1F805]" />
                    <span className="font-mono text-xs tracking-[0.2em] text-neutral-500">
                      {tCommon("brand")}
                    </span>
                  </Link>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <SearchButton />
                  <LanguageSwitcher />
                  <UserMenu />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">

                {/* Artist info */}
                <div className="flex items-start gap-4">
                  {artist.image_url && (
                    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-neutral-800">
                      <Image
                        src={artist.image_url}
                        alt={artist.name}
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <h1 className="font-mono text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                        {artist.name}
                      </h1>
                      <SubscribeButton
                        type="artist"
                        targetId={artist.id}
                        targetName={artist.name}
                        targetImage={artist.image_url}
                        size="md"
                      />
                      {artist.spotify_id && (
                        <SpotifyPlayButton
                          spotifyId={artist.spotify_id}
                          artistName={artist.name}
                          event={events.find((event) => event.attractions.some((billed) => billed.id === artist.id))}
                          attractionId={artist.id}
                        />
                      )}
                      <ArtistExternalLinks links={artist.external_links} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {(() => {
                        const genres: string[] = [];
                        for (const g of artist.genres ?? []) {
                          if (isDisplayableGenre(g) && !genres.includes(g)) genres.push(g);
                        }
                        // Also pull unique genres from the artist's events
                        const eventGenres = getUniqueGenres(events);
                        for (const g of eventGenres) {
                          if (!genres.includes(g) && g !== "Other") genres.push(g);
                        }
                        const visible = genresExpanded ? genres : genres.slice(0, 3);
                        return (
                          <>
                            {visible.map((genre) => (
                              <span
                                key={genre}
                                className="rounded-full border border-acid/30 px-2 py-0.5 font-mono text-[10px] text-acid"
                              >
                                {genre.toUpperCase()}
                              </span>
                            ))}
                            {genres.length > 3 && (
                              <button
                                onClick={() => setGenresExpanded(!genresExpanded)}
                                className="rounded-full border border-neutral-700 px-2 py-0.5 font-mono text-[10px] text-neutral-400 hover:text-neutral-300"
                              >
                                {genresExpanded ? tCommon("less") : `+${genres.length - 3}`}
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <p className="font-mono text-sm text-neutral-500">
                        {normalizeNumberGrouping(t("showCount", { count: filteredEvents.length }))}
                      </p>
                      <ReportButton
                        entityType="artist"
                        entityId={artist.id}
                        entityName={artist.name}
                      />
                      <AdminArtistEditButton artist={artist} />
                    </div>
                  </div>
                </div>
                {biography?.trim() || canListen ? (
                  <div className={`mt-4 max-w-prose ${artist.image_url ? "sm:ml-20" : ""}`}>
                    {biography?.trim() ? (
                      <p className="text-sm leading-relaxed text-neutral-300 sm:text-base">
                        {biography}
                      </p>
                    ) : null}
                    {canListen ? (
                      <button type="button" onClick={startArtistPlayer}
                        className="mt-1 inline-flex min-h-11 cursor-pointer items-start text-sm leading-relaxed text-acid underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-acid">
                        {t("listenToArtist", { artist: artist.name })}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="flex shrink-0 flex-col items-end gap-3">
                <div className="flex w-fit gap-1 rounded-lg border border-neutral-800 bg-surface p-1">
                  <button
                    onClick={() => setView("list")}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
                      view === "list"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    <List className="h-3.5 w-3.5" />
                    {t("list")}
                  </button>
                  <button
                    onClick={() => setView("grid")}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
                      view === "grid"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    {t("grid")}
                  </button>
                  <button
                    onClick={() => setView("map")}
                    onMouseEnter={() => {
                      (MapView as { preload?: () => void }).preload?.();
                      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
                      if (apiKey && !document.querySelector('script[src*="maps.googleapis.com"]')) {
                        const link = document.createElement("link");
                        link.rel = "preload";
                        link.as = "script";
                        link.href = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&loading=async`;
                        document.head.appendChild(link);
                      }
                    }}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
                      view === "map"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    <MapIcon className="h-3.5 w-3.5" />
                    {t("map")}
                  </button>
                </div>
                {hasHistory && (
                  <button
                    onClick={() => router.back()}
                    className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-neutral-700/50 text-neutral-500 hover:border-neutral-600 hover:text-white transition-all"
                    aria-label="Go back"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              </div>
            </div>

            {/* Filters */}
            <FilterBar
              genres={[]}
              otherSubGenres={[]}
              selectedGenres={[]}
              onGenresChange={() => {}}
              genreMatchMode="or"
              onMatchModeChange={() => {}}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              selectedTime={selectedTime}
              onTimeChange={setSelectedTime}
              onlyWithAudio={false}
              onOnlyWithAudioChange={() => {}}
              hideAudioFilter
            />
          </div>

          {view === "list" ? (
            <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${spotifyBarVisible ? "pb-[220px]" : "pb-12"}`}>
              <div className="lg:flex lg:items-start lg:gap-8">
                <div className="min-w-0 flex-1">
                  <EventsGrid
                    events={filteredEvents}
                    festivals={festivals}
                    blockedSpotifyIds={blockedSpotifyIds}
                    preferredArtistId={artist.id}
                    showCity
                    view="list"
                    fillWidth
                  />
                </div>
                <ListSidebar cityName={firstCityName} events={events} citySlug={firstCitySlug} />
              </div>
            </div>
          ) : view === "grid" ? (
            <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${spotifyBarVisible ? "pb-[220px]" : "pb-12"}`}>
              <EventsGrid
                events={filteredEvents}
                festivals={festivals}
                blockedSpotifyIds={blockedSpotifyIds}
                preferredArtistId={artist.id}
                showCity
              />
            </div>
          ) : (
            <div className="relative flex h-[calc(100vh-200px)]">
              <div className="min-w-0 flex-1">
                <MapView
                  center={mapCenter}
                  zoom={mapZoom}
                  venueGroups={venueGroups}
                  selectedVenueId={selectedVenueId}
                  onVenueSelect={handleVenueSelect}
                  onBoundsChanged={handleBoundsChanged}
                  blockedSpotifyIds={blockedSpotifyIds}
                />
              </div>
              {showSidebar && (
                <MapSidebar
                  events={visibleEvents}
                  venueGroups={visibleVenueGroups}
                  blockedSpotifyIds={blockedSpotifyIds}
                  citySlug={firstCitySlug}
                />
              )}
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className={`absolute top-3 hidden lg:flex items-center justify-center h-8 w-8 rounded-full border border-neutral-700 bg-dark/90 backdrop-blur-sm text-neutral-400 transition-colors hover:border-neutral-500 hover:text-white ${
                  showSidebar ? "right-[25rem]" : "right-3"
                }`}
                title={showSidebar ? t("hideSidebar") : t("showSidebar")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className={`transition-transform ${showSidebar ? "" : "rotate-180"}`}
                >
                  <path
                    d="M5.25 3.5L8.75 7L5.25 10.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {similarArtists.length > 0 && (
            <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${spotifyBarVisible ? "pb-[220px]" : "pb-12"}`}>
              <h2 className="mb-4 font-mono text-sm uppercase tracking-widest text-neutral-400">
                {t("similarArtists")}
              </h2>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {similarArtists.map((sa) => (
                  <Link
                    key={sa.id}
                    href={`/artist/${sa.slug}`}
                    rel={artistLinkRel(sa, sa.hasUpcomingShows)} prefetch={false}
                    className="group flex shrink-0 items-center gap-3 rounded-lg border border-neutral-800 bg-surface px-3 py-2 transition-colors hover:border-neutral-600"
                  >
                    {sa.image_url ? (
                      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md">
                        <Image
                          src={sa.image_url}
                          alt={sa.name}
                          fill
                          className="object-cover"
                          sizes="40px"
                        />
                      </div>
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-500">
                        <span className="font-mono text-xs">{sa.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm text-white group-hover:text-acid">
                        {sa.name}
                      </p>
                      {sa.genres.length > 0 && (
                        <div className="mt-1 flex gap-1">
                          {sa.genres.filter(isDisplayableGenre).slice(0, 2).map((g) => (
                            <span
                              key={g}
                              className="rounded-full border border-acid/20 px-1.5 py-0 font-mono text-[9px] text-acid/70"
                            >
                              {g.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
        {relatedJukebox.modalProps && <GenreJukeboxModal
          {...relatedJukebox.modalProps}
          key={relatedJukebox.modalKey}
          genreName={tJukebox("allArtists")}
          selectedGenres={[]}
        />}
    </AuthProvider>
  );
}
